import { useMemo } from 'react'

import { AmountModel, type Expense as ExpenseType, type Income } from '@subaio/api-types/budget'
import { ChevronFlip, createSpinnerTabler, FormattedNumber } from '@subaio/components'

import { Cell, HeaderRow, ItemExpandable } from 'components/Lists'

import { EmptyChevron } from '../EmptyChevron'
import { Service } from '../Service'
import { Input } from './Input'

const Spinner = createSpinnerTabler(({ theme }) => ({
	size: 28,
	color: theme.color.text,
	backgroundColor: theme.color.background,
}))

type Props = {
	id: string
	name: string

	currency: string
	expenses?: Array<ExpenseType>
	fieldsBeingSubmitted: Array<string>
	income?: Array<Income>
	totalMonthlyAmount: number
	totalMonthlyAmountUserProvided: number | null
	amountModel: AmountModel
}

export const Subcategory = ({
	id,
	name,
	currency = 'DKK',
	totalMonthlyAmount,
	totalMonthlyAmountUserProvided,
	expenses,
	income,
	fieldsBeingSubmitted,
	amountModel,
}: Props) => {
	const hasIncome = income && income.length > 0
	const hasServices = (expenses && expenses.length > 0) || hasIncome
	const isBeingSubmitted = fieldsBeingSubmitted.includes(id)

	const amountToShow = useMemo(() => {
		if (amountModel === AmountModel.AmountAverageLastThreeMonths) return 'threeMonthsAverage'
		if (amountModel === AmountModel.AmountAverageLastTwelveMonths) return 'twelveMonthsAverage'
		return 'default'
	}, [amountModel])

	return (
		<ItemExpandable
			renderRow={({ isExpanded, ...rest }) => (
				<HeaderRow {...rest}>
					<Cell textAlign='center'>{hasServices ? <ChevronFlip isActive={isExpanded} /> : <EmptyChevron />}</Cell>
					<Cell shouldExpand>{name}</Cell>
					{(() => {
						if (fieldsBeingSubmitted.includes(id)) {
							return (
								<Cell>
									<Spinner />
								</Cell>
							)
						}
						if (totalMonthlyAmountUserProvided !== null) {
							return (
								<Cell>
									{/* This is the only time we want to show `0` if that is the starting point */}
									(<FormattedNumber value={totalMonthlyAmount} currency={currency} format='integer-currency' />)
								</Cell>
							)
						}
					})()}
					<Cell>
						<Input
							label=''
							name={`categories.${id}.amount`}
							showOptionalHint={false}
							type='number'
							autoComplete='off'
							isDisabled={isBeingSubmitted}
							min={0}
							validate={{ wholeNumber: (value) => /^\d*$/.test(value) || value === undefined }}
							renderSuffix={() => <span>kr.</span>}
							showValidationMessage={false}
							onClick={(event) => {
								event.stopPropagation()
							}}
							onBlur={(event) => {
								if (event.currentTarget.value === '' && totalMonthlyAmountUserProvided === null) {
									event.currentTarget.value = Math.round(totalMonthlyAmount).toString()
								}
							}}
						/>
					</Cell>
				</HeaderRow>
			)}
			isDisabled={!hasServices}
		>
			{expenses
				?.filter((expense) => expense.isUsedInCategoryAmount)
				.map((expense, index) => (
					<Service key={index} amountToShow={amountToShow} {...expense} />
				))}
			{income
				?.filter((expense) => expense.isUsedInCategoryAmount)
				.map((income, index) => (
					<Service key={index} amountToShow={amountToShow} {...income} />
				))}
		</ItemExpandable>
	)
}
